/**
 * API Service
 * Handles all API calls to the backend
 */

const API_URL = process.env.REACT_APP_API_URL || '/api';

class ApiService {
  constructor() {
    this.token = localStorage.getItem('token');
  }

  setToken(token) {
    this.token = token;
    if (token) {
      localStorage.setItem('token', token);
    } else {
      localStorage.removeItem('token');
    }
  }

  getToken() {
    return this.token || localStorage.getItem('token');
  }

  async request(endpoint, options = {}) {
    const url = `${API_URL}${endpoint}`;
    const token = this.getToken();
    
    const config = {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
    };

    if (options.body && typeof options.body === 'object') {
      config.body = JSON.stringify(options.body);
    }

    try {
      const response = await fetch(url, config);
      const data = await response.json();

      if (!response.ok) {
        // Handle 401 (unauthorized) - token expired or invalid
        if (response.status === 401) {
          this.setToken(null);
          // Redirect to login if not already there
          if (!window.location.pathname.includes('/login')) {
            window.location.href = '/login';
          }
        }
        throw new Error(data.error || data.message || 'Request failed');
      }

      return data;
    } catch (error) {
      throw error;
    }
  }

  // Auth methods
  async login(email, password) {
    const data = await this.request('/auth/login', {
      method: 'POST',
      body: { email, password },
    });
    if (data.token) {
      this.setToken(data.token);
    }
    return data;
  }

  async register(name, email, password, role = 'student') {
    const data = await this.request('/auth/register', {
      method: 'POST',
      body: { name, email, password, role },
    });
    if (data.token) {
      this.setToken(data.token);
    }
    return data;
  }

  async forgotPassword(email) {
    return this.request('/auth/forgot-password', {
      method: 'POST',
      body: { email },
    });
  }

  async resetPassword(token, password) {
    return this.request('/auth/reset-password', {
      method: 'POST',
      body: { token, password },
    });
  }

  async googleLogin(accessToken, role = 'student') {
    const data = await this.request('/auth/google', {
      method: 'POST',
      body: { accessToken, role },
    });
    if (data.token) {
      this.setToken(data.token);
    }
    return data;
  }

  async getMe() {
    const data = await this.request('/auth/me', {
      method: 'GET',
    });
    return data;
  }

  async logout() {
    try {
      await this.request('/auth/logout', {
        method: 'POST',
      });
    } catch (error) {
      // Ignore errors on logout
    } finally {
      this.setToken(null);
    }
  }

  // User methods
  async getUserProfile() {
    return this.request('/users/me', {
      method: 'GET',
    });
  }

  async updateUserProfile(updates) {
    return this.request('/users/me', {
      method: 'PUT',
      body: updates,
    });
  }

  // Course methods
  async getCourses(params = {}) {
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
        queryParams.append(key, params[key]);
      }
    });
    const queryString = queryParams.toString();
    return this.request(`/courses${queryString ? `?${queryString}` : ''}`, {
      method: 'GET',
    });
  }

  async getAllCourses(params = {}) {
    // Admin endpoint to get all courses including drafts
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
        queryParams.append(key, params[key]);
      }
    });
    const queryString = queryParams.toString();
    return this.request(`/courses${queryString ? `?${queryString}` : ''}`, {
      method: 'GET',
    });
  }

  async getCourse(courseId) {
    return this.request(`/courses/${courseId}`, {
      method: 'GET',
    });
  }

  // Enrollment methods
  async enrollInCourse(courseId) {
    return this.request('/enrollments', {
      method: 'POST',
      body: { courseId },
    });
  }

  async getEnrollments(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/enrollments/student?${queryString}`, {
      method: 'GET',
    });
  }

  // Admin - User Management methods
  async getUsers(params = {}) {
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
        queryParams.append(key, params[key]);
      }
    });
    const queryString = queryParams.toString();
    return this.request(`/admin/users${queryString ? `?${queryString}` : ''}`, {
      method: 'GET',
    });
  }

  async getUser(userId) {
    return this.request(`/admin/users/${userId}`, {
      method: 'GET',
    });
  }

  async createUser(userData) {
    return this.request('/admin/users', {
      method: 'POST',
      body: userData,
    });
  }

  async updateUser(userId, userData) {
    return this.request(`/admin/users/${userId}`, {
      method: 'PUT',
      body: userData,
    });
  }

  async updateUserStatus(userId, status) {
    return this.request(`/admin/users/${userId}/status`, {
      method: 'PUT',
      body: { status },
    });
  }

  async getTeachers() {
    return this.request('/admin/teachers', {
      method: 'GET',
    });
  }

  async getAdminStats() {
    return this.request('/admin/stats', {
      method: 'GET',
    });
  }

  // Teacher - Assigned Courses methods
  async getAssignedCourses(filter = 'all') {
    return this.request(`/courses/teacher/assigned?filter=${filter}`, {
      method: 'GET',
    });
  }

  // Admin - Course methods
  async createCourse(courseData) {
    return this.request('/courses', {
      method: 'POST',
      body: courseData,
    });
  }

  async updateCourse(courseId, courseData) {
    return this.request(`/courses/${courseId}`, {
      method: 'PUT',
      body: courseData,
    });
  }

  async publishCourse(courseId) {
    return this.request(`/courses/${courseId}/publish`, {
      method: 'POST',
    });
  }

  async deleteCourse(courseId) {
    return this.request(`/courses/${courseId}`, {
      method: 'DELETE',
    });
  }

  // Subscription methods
  async getMySubscription() {
    return this.request('/subscriptions/my-subscription', {
      method: 'GET',
    });
  }

  async getSubscriptions(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/subscriptions?${queryString}`, {
      method: 'GET',
    });
  }

  async getSubscriptionStats() {
    return this.request('/subscriptions/stats', {
      method: 'GET',
    });
  }

  async createSubscription(data) {
    return this.request('/subscriptions', {
      method: 'POST',
      body: data,
    });
  }

  async updateSubscription(subscriptionId, data) {
    return this.request(`/subscriptions/${subscriptionId}`, {
      method: 'PUT',
      body: data,
    });
  }

  async cancelSubscription(subscriptionId) {
    return this.request(`/subscriptions/${subscriptionId}`, {
      method: 'DELETE',
    });
  }

  async deleteSubscription(subscriptionId) {
    return this.request(`/subscriptions/${subscriptionId}`, {
      method: 'DELETE',
    });
  }

  async getStudentSubscription(studentId) {
    return this.request(`/subscriptions/student/${studentId}`, {
      method: 'GET',
    });
  }

  // Class methods
  async getClassesByCourse(courseId) {
    return this.request(`/classes/course/${courseId}`, { method: 'GET' });
  }

  async createClass(data) {
    return this.request('/classes', { method: 'POST', body: data });
  }

  async updateClass(classId, data) {
    return this.request(`/classes/${classId}`, { method: 'PUT', body: data });
  }

  async deleteClass(classId) {
    return this.request(`/classes/${classId}`, { method: 'DELETE' });
  }

  async publishClass(classId) {
    return this.request(`/classes/${classId}/publish`, { method: 'POST' });
  }

  // Enrollment Request methods
  async createEnrollmentRequest(data) {
    return this.request('/enrollment-requests', { method: 'POST', body: data });
  }

  async getEnrollmentRequests(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/enrollment-requests?${queryString}`, { method: 'GET' });
  }

  async checkEnrollmentRequest(courseId) {
    return this.request(`/enrollment-requests/check/${courseId}`, { method: 'GET' });
  }

  async getMyEnrollmentRequests() {
    return this.request('/enrollment-requests/my-requests', { method: 'GET' });
  }

  async approveEnrollmentRequest(requestId, data) {
    return this.request(`/enrollment-requests/${requestId}/approve`, { method: 'PUT', body: data });
  }

  async rejectEnrollmentRequest(requestId, data = {}) {
    return this.request(`/enrollment-requests/${requestId}/reject`, { method: 'PUT', body: data });
  }

  // Contact
  async sendContactMessage(data) {
    return this.request('/contact', { method: 'POST', body: data });
  }
}

const apiService = new ApiService();
export default apiService;

